import { useMemo, useState, useEffect, useRef } from 'react';
import ReactConfetti from 'react-confetti';
import { ViewDataButton, OrderContainer } from '../lib/ui-kit/index.js';
import type { PurchaseHistory } from '../modules/DataTransformSchema';
import { useTenant } from '../FeatureFlags';
import { apiClient } from '../api';
import { useAnalytics } from '../hooks/useAnalytics';
import { useOverlayVariant } from '../hooks/useOverlayVariant';
import {
  OrderGrid,
  OrderGridSkeleton,
  OrderGridEmpty,
} from '../components/OrderGrid';
import { OrderList, OrderListSkeleton } from '../components/OrderList';
import { OrderDetailOverlay } from '../components/OrderDetailOverlay';
import { DownloadButton } from '../components/DownloadButton';
import { TeaTimeModal } from '../components/TeaTimeModal';
import { InsightsTabContent } from '../components/InsightsTabContent';
import { ConfirmationCode } from '../components/ConfirmationCode';
import { getOrderDate, MS_PER_DAY } from '../utils';
import type { InsightsResponse } from '../types/insights';
import { clientLogger } from '../utils/client-logger';
import { postMessageChangeStep } from '../utils/postMessage';
import { useFeatureFlag } from '../FeatureFlags';

type DashboardTab = 'orders' | 'insights';

type OrdersViewMode = 'grid' | 'list';

const ORDERS_VIEW_STORAGE_KEY = 'tapConnect.ordersViewMode';

type DashboardPageProps = {
  orders: PurchaseHistory[];
  onRetryConnection?: () => void;
  onConnectAnother?: () => void;
  isEmpty?: boolean;
  isLoadingMore?: boolean;
  sessionId?: string | null;
  isQuestionProTask?: boolean;
  startConfirmed?: boolean;
  retailerName?: string;
  syncError?: string | null;
};

export function DashboardPage({
  orders,
  onRetryConnection,
  onConnectAnother,
  isEmpty = false,
  isLoadingMore = false,
  sessionId,
  isQuestionProTask = false,
  startConfirmed = false,
  retailerName = 'Amazon',
  syncError = null,
}: DashboardPageProps) {
  const { track } = useAnalytics();
  const tenant = useTenant();
  const arioView = tenant.flow.chromeless;
  const [isConfirmed, setConfirmed] = useState(startConfirmed);
  const [showConfetti, setShowConfetti] = useState(true);
  const [windowDimensions, setWindowDimensions] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<DashboardTab>('orders');
  const [search, setSearch] = useState('');
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [ordersView, setOrdersView] = useState<OrdersViewMode>(
    () =>
      (localStorage.getItem(
        ORDERS_VIEW_STORAGE_KEY
      ) as OrdersViewMode | null) ?? 'grid'
  );
  const [selectedOrder, setSelectedOrder] = useState<PurchaseHistory | null>(
    null
  );
  const [insightsData, setInsightsData] = useState<InsightsResponse | null>(
    null
  );
  const [isInsightsLoading, setIsInsightsLoading] = useState(false);
  const [insightsError, setInsightsError] = useState<string | null>(null);
  const [hasShownInsightsModal, setHasShownInsightsModal] = useState(false);
  const isInsightsEnabled = useFeatureFlag('insight_pills');
  const showInsightsTab = isInsightsEnabled;
  const overlayVariant = useOverlayVariant(windowDimensions.width);
  const visibleTabs: { id: DashboardTab; label: string }[] = [
    { id: 'orders', label: 'Orders' },
    ...(showInsightsTab
      ? [{ id: 'insights' as DashboardTab, label: 'Insights' }]
      : []),
  ];

  const sortedOrders = useMemo(() => {
    return [...orders].sort((a, b) => {
      const dateA = getOrderDate(a);
      const dateB = getOrderDate(b);
      const timeA = dateA ? dateA.getTime() : -Infinity;
      const timeB = dateB ? dateB.getTime() : -Infinity;
      return timeB - timeA;
    });
  }, [orders]);

  const visibleOrders = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return sortedOrders;
    return sortedOrders.filter((order) =>
      (order.product_name ?? '').toLowerCase().includes(query)
    );
  }, [sortedOrders, search]);

  // Summary stats for the dashboard header (Orders / Items / Total spend)
  const summaryStats = useMemo(() => {
    const items = orders.reduce(
      (sum, o) =>
        sum +
        (typeof o.quantity === 'number' && o.quantity > 0 ? o.quantity : 1),
      0
    );
    const spend = orders.reduce((sum, o) => {
      const n = parseFloat(
        String(o.product_price ?? o.order_total ?? '').replace(/[^0-9.]/g, '')
      );
      return sum + (isNaN(n) ? 0 : n);
    }, 0);
    // Orders = unique order_id; items without an order_id count individually.
    const orderIds = new Set<string>();
    let looseOrders = 0;
    orders.forEach((o) => {
      if (o.order_id) orderIds.add(o.order_id);
      else looseOrders++;
    });
    return {
      orders: orderIds.size + looseOrders,
      items,
      spend: `$${spend.toFixed(0)}`,
    };
  }, [orders]);

  // Categorize orders into upcoming deliveries and past orders
  const upcomingDeliveries = useMemo(() => {
    const today = new Date();
    const startOfToday = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate()
    );
    return sortedOrders.filter((order) => {
      const orderDate = getOrderDate(order);
      if (!orderDate) return false;
      const daysSinceOrder = Math.floor(
        (startOfToday.getTime() - orderDate.getTime()) / MS_PER_DAY
      );
      // Consider orders from last 7 days as upcoming
      return daysSinceOrder <= 7;
    });
  }, [sortedOrders]);

  const pastOrders = useMemo(() => {
    const today = new Date();
    const startOfToday = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate()
    );
    return sortedOrders.filter((order) => {
      const orderDate = getOrderDate(order);
      if (!orderDate) return false;
      const daysSinceOrder = Math.floor(
        (startOfToday.getTime() - orderDate.getTime()) / MS_PER_DAY
      );
      // Consider orders older than 7 days as past orders
      return daysSinceOrder > 7;
    });
  }, [sortedOrders]);

  const onConfirmClicked = async () => {
    postMessageChangeStep(4);
    setLoading(true);
    setShowConfetti(false);

    track('Purchase Confirmation Started');

    try {
      const response = await apiClient.submitData({
        totalPastOrders: pastOrders.length,
        totalUpcomingDeliveries: upcomingDeliveries.length,
      });
      clientLogger.info('Response:', { response });
      track('Purchase Confirmation Completed');
    } catch (error) {
      clientLogger.info('Error submitting data:', { error });
      track('Purchase Confirmation Failed', {
        error: error instanceof Error ? error.message : 'Unknown error',
      });
    }

    setLoading(false);
    setConfirmed(true);
  };

  const loadInsights = async () => {
    if (isInsightsLoading || !showInsightsTab) return;

    setIsInsightsLoading(true);
    setInsightsError(null);

    track('Insights Load Started');

    try {
      const data = await apiClient.getInsights();
      setInsightsData(data);
      track('Insights Load Succeeded');
    } catch (error) {
      clientLogger.info('Error loading insights:', { error });
      setInsightsError(
        error instanceof Error ? error.message : 'Failed to load insights'
      );
      track('Insights Load Failed', {
        error: error instanceof Error ? error.message : 'Unknown error',
      });
    } finally {
      setIsInsightsLoading(false);
    }
  };

  const handleOrdersViewChange = (view: OrdersViewMode) => {
    setOrdersView(view);
    localStorage.setItem(ORDERS_VIEW_STORAGE_KEY, view);
    track('Orders View Changed', { view });
  };

  const handleTabChange = (tab: DashboardTab) => {
    if (tab === 'insights' && !showInsightsTab) {
      return;
    }

    setActiveTab(tab);

    if (tab === 'insights') {
      track('Insights Tab Clicked');

      if (!insightsData && !isInsightsLoading) {
        if (!hasShownInsightsModal) {
          setHasShownInsightsModal(true);
        }
        void loadInsights();
      }
    }
  };

  useEffect(() => {
    let confettiTimer: NodeJS.Timeout;
    if (showConfetti) {
      confettiTimer = setTimeout(() => {
        setShowConfetti(false);
      }, 10 * 1000); // 10 seconds
    }
    return () => {
      if (confettiTimer) {
        clearTimeout(confettiTimer);
      }
    };
  }, [showConfetti]);

  useEffect(() => {
    postMessageChangeStep(3);

    const handleResize = () => {
      setWindowDimensions({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // When arriving pre-confirmed (from SuccessPage), submit data in background for tracking
  useEffect(() => {
    if (!startConfirmed) return;
    track('Purchase Confirmation Started');
    apiClient
      .submitData({
        totalPastOrders: pastOrders.length,
        totalUpcomingDeliveries: upcomingDeliveries.length,
      })
      .then(() => track('Purchase Confirmation Completed'))
      .catch((error) =>
        track('Purchase Confirmation Failed', {
          error: error instanceof Error ? error.message : 'Unknown error',
        })
      );
    // oxlint-disable-next-line react/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen bg-surface">
      {showConfetti && (
        <ReactConfetti
          width={windowDimensions.width}
          height={windowDimensions.height}
          recycle={true}
          numberOfPieces={500}
          gravity={0.1}
        />
      )}
      {/* Header */}
      <div
        className={`bg-surface ${arioView && isConfirmed ? 'border-b border-rule mb-6' : ''}`}
      >
        <div
          className={`max-w-6xl mx-auto px-6 ${arioView && isConfirmed ? 'py-4 text-left' : 'py-8 text-center'}`}
        >
          {arioView && isConfirmed && (
            <h1 className="font-mono text-sm font-medium uppercase tracking-widest text-primary antialiased">
              ARIO DATA VIEW
            </h1>
          )}

          {/* Confirmation Code Display */}
          {tenant.flow.showConfirmationCode && sessionId && (
            <ConfirmationCode
              label={
                isQuestionProTask
                  ? 'QuestionPro confirmation code'
                  : `Tap ${retailerName} confirmation code`
              }
              code={sessionId.slice(-5)}
            />
          )}

          {/* Tea Time Modal */}
          <TeaTimeModal isLoadingMore={isLoadingMore} />

          {syncError && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6 text-left max-w-2xl mx-auto">
              <p className="text-sm font-semibold text-red-800 mb-1">
                Some orders could not be loaded
              </p>
              <p className="text-sm text-red-700 mb-3">{syncError}</p>
              {onRetryConnection && (
                <button
                  data-testid="dashboard-sync-error-retry-button"
                  onClick={() => {
                    track('Retry Connection Clicked', {
                      reason: 'partial_sync_error',
                    });
                    onRetryConnection();
                  }}
                  className="bg-primary hover:bg-primary-deep text-white px-4 py-2 rounded-xl text-sm font-semibold transition-colors"
                >
                  Try Again
                </button>
              )}
            </div>
          )}

          {/* Confirmation CTA - floating button */}
          {tenant.flow.viewData && !isEmpty && !isConfirmed && (
            <ViewDataButton onClick={onConfirmClicked} loading={loading} />
          )}

          {/* Empty State Section */}
          {isEmpty && (
            <div className="bg-paper border border-rule-light rounded-[20px] p-8 mb-8 text-center">
              <div className="w-[52px] h-[52px] mx-auto mb-4 flex items-center justify-center rounded-2xl bg-primary-pale text-primary">
                <svg
                  className="w-6 h-6"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zm0 4a1 1 0 011-1h6a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h6a1 1 0 110 2H4a1 1 0 01-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <h3 className="font-display text-lg font-bold text-ink mb-2">
                No Orders Found
              </h3>
              <p className="text-ink-muted mb-6">
                We couldn't find any purchases in your {retailerName} account.
                This might be because you haven't made any purchases recently,
                or they might be in a different account.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <button
                  data-testid="dashboard-empty-retry-button"
                  onClick={() => {
                    track('Retry Connection Clicked', {
                      reason: 'empty_results',
                    });
                    onRetryConnection?.();
                  }}
                  className="bg-primary hover:bg-primary-deep text-white px-6 py-2 rounded-xl font-semibold transition-colors"
                >
                  Try Again
                </button>
                <button
                  data-testid="dashboard-empty-connect-another-button"
                  onClick={() => {
                    track('Connect Different Account Clicked', {
                      reason: 'empty_results',
                    });
                    onConnectAnother?.();
                  }}
                  className="bg-ink-soft hover:bg-ink text-white px-6 py-2 rounded-xl font-semibold transition-colors"
                >
                  Connect Different Account
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Thank you section */}
      {!isConfirmed && (
        <div className="flex items-center justify-center py-10 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="max-w-xl w-full">
            <div className="rounded-2xl border border-yellow-300 bg-yellow-50 px-8 py-10 text-center shadow-sm">
              <div className="text-4xl mb-3">🎉</div>
              <h2 className="text-2xl font-semibold text-gray-900 mb-2">
                Thank You
              </h2>
              <p className="text-gray-700">
                We&apos;ll email your {retailerName} gift card within 24 hours.
              </p>
            </div>
          </div>
        </div>
      )}
      {/* Main Content - only show after confirmation */}
      {!isEmpty && isConfirmed && (
        <div
          className={`max-w-6xl mx-auto px-6 py-8 ${arioView ? 'pt-0' : ''}`}
        >
          {/* Brand row + connected retailer */}
          <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
            <div className="font-display text-[15px] font-bold tracking-[0.01em] text-ink">
              Tap Connect
            </div>
            <div className="flex items-center gap-1.5 rounded-xl bg-paper-warm px-4 py-2">
              <span className="h-1.5 w-1.5 flex-shrink-0 rounded-full bg-success" />
              <span className="text-[12.5px] font-semibold text-ink">
                {retailerName}
              </span>
            </div>
          </div>

          {/* Summary stats */}
          <div className="mb-[22px] flex flex-wrap gap-2.5">
            {[
              { value: summaryStats.orders, label: 'Orders' },
              { value: summaryStats.items, label: 'Items' },
              { value: summaryStats.spend, label: 'Total spend' },
            ].map((s) => (
              <div
                key={s.label}
                className="min-w-[150px] flex-1 rounded-2xl bg-paper-warm px-5 py-[18px]"
              >
                <b className="block font-display text-[28px] font-bold text-ink">
                  {s.value}
                </b>
                <span className="text-[11.5px] font-medium uppercase tracking-[0.06em] text-ink-soft">
                  {s.label}
                </span>
              </div>
            ))}
          </div>

          {/* Tab Navigation — segmented control matching the retailer switcher.
              Only rendered when there's more than one tab to switch between
              (Insights is gated behind the insight_pills flag). */}
          {visibleTabs.length > 1 && (
            <div className="mb-6 border-b border-rule-light pb-6">
              <nav
                className="inline-flex gap-0.5 rounded-xl bg-paper-warm p-1"
                aria-label="Tabs"
              >
                {visibleTabs.map((tab) => (
                  <button
                    key={tab.id}
                    data-testid={`dashboard-tab-${tab.id}`}
                    onClick={() => handleTabChange(tab.id)}
                    aria-current={activeTab === tab.id ? 'page' : undefined}
                    className={`cursor-pointer rounded-[9px] px-4 py-2 text-[12.5px] font-semibold transition-colors ${
                      activeTab === tab.id
                        ? 'bg-surface text-ink shadow-[0_1px_2px_rgba(0,0,0,0.06)]'
                        : 'text-ink-muted hover:text-ink'
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </nav>
            </div>
          )}

          {/* Tab Content */}
          {activeTab === 'orders' && (
            <OrderContainer>
              <div className="mb-[18px] flex items-center gap-2.5">
                <h2 className="font-display text-xl font-bold text-ink flex items-center">
                  Your Orders
                </h2>
              </div>

              <div className="mb-[18px] flex flex-wrap items-center justify-between gap-2.5">
                <div className="relative flex w-full basis-full items-center sm:w-auto sm:min-w-[180px] sm:max-w-[280px] sm:flex-1 sm:basis-auto">
                  <input
                    ref={searchInputRef}
                    type="text"
                    data-testid="dashboard-search-input"
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    placeholder="Search products…"
                    aria-label="Search products"
                    className="w-full rounded-full border border-rule py-2.5 pl-4 pr-10 text-[13px] text-ink outline-none placeholder:text-ink-faint focus:border-primary"
                  />
                  {search && (
                    <button
                      type="button"
                      data-testid="dashboard-search-clear-button"
                      onClick={() => {
                        setSearch('');
                        searchInputRef.current?.focus();
                      }}
                      aria-label="Clear search"
                      className="absolute right-2 flex h-6 w-6 cursor-pointer items-center justify-center rounded-full bg-paper-warm text-[11px] leading-none text-ink-muted transition-colors hover:bg-rule hover:text-ink"
                    >
                      ✕
                    </button>
                  )}
                </div>
                <div className="flex w-full items-center justify-between gap-2.5 sm:w-auto sm:justify-start">
                  <div
                    className="inline-flex gap-0.5 rounded-xl bg-paper-warm p-1"
                    role="group"
                    aria-label="Orders view"
                  >
                    {(
                      [
                        { id: 'grid', label: 'Grid' },
                        { id: 'list', label: 'List' },
                      ] as { id: OrdersViewMode; label: string }[]
                    ).map((view) => (
                      <button
                        key={view.id}
                        type="button"
                        data-testid={`orders-view-${view.id}`}
                        onClick={() => handleOrdersViewChange(view.id)}
                        aria-pressed={ordersView === view.id}
                        className={`cursor-pointer rounded-[9px] px-3 py-1.5 text-[12px] font-semibold transition-colors ${
                          ordersView === view.id
                            ? 'bg-surface text-ink shadow-[0_1px_2px_rgba(0,0,0,0.06)]'
                            : 'text-ink-muted hover:text-ink'
                        }`}
                      >
                        {view.label}
                      </button>
                    ))}
                  </div>
                  <DownloadButton orders={sortedOrders} />
                </div>
              </div>

              {orders.length === 0 && isLoadingMore ? (
                ordersView === 'grid' ? (
                  <OrderGridSkeleton count={6} />
                ) : (
                  <OrderListSkeleton count={5} />
                )
              ) : visibleOrders.length === 0 ? (
                <OrderGridEmpty
                  message={
                    search.trim()
                      ? 'Try a different search term.'
                      : 'No orders to show yet.'
                  }
                />
              ) : ordersView === 'grid' ? (
                <OrderGrid
                  orders={visibleOrders}
                  onSelectOrder={setSelectedOrder}
                  trailingSkeletonCount={isLoadingMore ? 3 : 0}
                />
              ) : (
                <OrderList
                  orders={visibleOrders}
                  onSelectOrder={setSelectedOrder}
                  trailingSkeletonCount={isLoadingMore ? 3 : 0}
                />
              )}
            </OrderContainer>
          )}

          {showInsightsTab && activeTab === 'insights' && (
            <InsightsTabContent
              data={insightsData}
              isLoading={isInsightsLoading}
              error={insightsError}
              onRetry={loadInsights}
            />
          )}
        </div>
      )}

      {selectedOrder && (
        <OrderDetailOverlay
          order={selectedOrder}
          variant={overlayVariant}
          onClose={() => setSelectedOrder(null)}
        />
      )}
    </div>
  );
}
